/* ------------------------------------------------------------------ *
 * The simulated result of GHOSTSHIP's AIS_DARK_SCAN command: the ships
 * the satellite's AIS receiver hears over the target sea area, and the
 * ones that should be there (from their earlier tracks) but stay silent --
 * the "dark" ships the mission is looking for. Invented, but plausible:
 * real MMSI country codes, typical ship types, speeds and message counts.
 * Deterministic per group, so all members see the same ships.
 * ------------------------------------------------------------------ */

export type ScanArea = { name: string; latMin: number; latMax: number; lonMin: number; lonMax: number };

// A sample target area; a known hotspot for illegal fishing.
export const AIS_TARGET_AREA: ScanArea = { name: "Gulf of Guinea, off West Africa", latMin: -1, latMax: 6, lonMin: -4, lonMax: 6 };

export type Vessel = {
  mmsi: string;
  name: string;
  flag: string;
  type: string;
  latDeg: number;
  lonDeg: number;
  speedKn: number;
  // AIS messages received from it during the scan; 0 for a dark ship.
  messages: number;
  dark: boolean;
  // For a dark ship: when it was last heard, and where it should have been.
  lastHeardHoursAgo?: number;
};

export type AisScan = { area: ScanArea; vessels: Vessel[]; totalMessages: number; dataKb: number };

// Country codes at the start of an MMSI (Maritime Identification Digits).
const FLAGS: { flag: string; mid: string }[] = [
  { flag: "Liberia", mid: "636" },
  { flag: "Panama", mid: "352" },
  { flag: "Marshall Islands", mid: "538" },
  { flag: "China", mid: "412" },
  { flag: "Ghana", mid: "627" },
  { flag: "Nigeria", mid: "657" },
  { flag: "Spain", mid: "224" },
  { flag: "South Korea", mid: "440" },
  { flag: "Togo", mid: "671" },
  { flag: "Cameroon", mid: "613" },
];

const TYPES: { type: string; speed: [number, number]; weight: number }[] = [
  { type: "Fishing", speed: [2, 9], weight: 5 },
  { type: "Cargo", speed: [10, 16], weight: 3 },
  { type: "Tanker", speed: [9, 14], weight: 2 },
  { type: "Reefer", speed: [8, 14], weight: 1 },
];

const NAME_PARTS = [
  ["ATLANTIC", "OCEAN", "SEA", "GOLDEN", "SILVER", "BLUE", "CAPE", "NORTH", "SOUTHERN", "BRIGHT", "GRAND", "CORAL"],
  ["STAR", "PRIDE", "TIDE", "HORIZON", "WAVE", "SPIRIT", "VOYAGER", "HARVEST", "DAWN", "BREEZE", "FORTUNE", "MARINER"],
];

// Small seeded random generator (mulberry32), so a group always gets the
// same ships.
function random(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function generateAisScan(seed: number, area: ScanArea = AIS_TARGET_AREA): AisScan {
  const rnd = random(seed);
  const pick = <T,>(list: T[]) => list[Math.floor(rnd() * list.length)];
  const between = (min: number, max: number) => min + rnd() * (max - min);
  const totalWeight = TYPES.reduce((sum, t) => sum + t.weight, 0);
  const pickType = () => {
    let r = rnd() * totalWeight;
    for (const t of TYPES) {
      r -= t.weight;
      if (r <= 0) return t;
    }
    return TYPES[0];
  };

  const count = 20 + Math.floor(rnd() * 9);
  const darkCount = 3 + Math.floor(rnd() * 3);
  const usedNames = new Set<string>();
  const vessels: Vessel[] = [];

  for (let i = 0; i < count; i++) {
    // Dark ships are fishing vessels: switching AIS off to fish unseen is
    // what the mission is about.
    const dark = i < darkCount;
    const t = dark ? TYPES[0] : pickType();
    const flag = pick(FLAGS);
    let name = "";
    do {
      name = `${pick(NAME_PARTS[0])} ${pick(NAME_PARTS[1])}${rnd() < 0.4 ? ` ${1 + Math.floor(rnd() * 30)}` : ""}`;
    } while (usedNames.has(name));
    usedNames.add(name);

    vessels.push({
      mmsi: flag.mid + String(Math.floor(rnd() * 1e6)).padStart(6, "0"),
      name,
      flag: flag.flag,
      type: t.type,
      latDeg: Math.round(between(area.latMin, area.latMax) * 1000) / 1000,
      lonDeg: Math.round(between(area.lonMin, area.lonMax) * 1000) / 1000,
      speedKn: Math.round(between(t.speed[0], t.speed[1]) * 10) / 10,
      messages: dark ? 0 : 4 + Math.floor(rnd() * 27),
      dark,
      lastHeardHoursAgo: dark ? 6 + Math.floor(rnd() * 40) : undefined,
    });
  }

  // Dark ships first, then by most messages.
  vessels.sort((a, b) => Number(b.dark) - Number(a.dark) || b.messages - a.messages);
  const totalMessages = vessels.reduce((sum, v) => sum + v.messages, 0);
  // An AIS position report is 168 bits; with timestamps and framing
  // stored on board, about 40 bytes each.
  return { area, vessels, totalMessages, dataKb: Math.max(1, Math.round((totalMessages * 40) / 1024)) };
}

export function formatLatLon(latDeg: number, lonDeg: number): string {
  const lat = `${Math.abs(latDeg).toFixed(3)}°${latDeg >= 0 ? "N" : "S"}`;
  const lon = `${Math.abs(lonDeg).toFixed(3)}°${lonDeg >= 0 ? "E" : "W"}`;
  return `${lat} ${lon}`;
}
